import { Link } from 'react-router-dom';

export default function SignupsPage() {
    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-2xl mb-4 shadow-lg">
                    <span className="text-3xl">📝</span>
                </div>
                <div className="inline-block bg-primary-950 text-primary-300 border border-primary-500/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                    Fall 2026 Registration
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-extrabold text-white mb-3">
                    Fall 2026 <span className="text-primary-500">Signups</span>
                </h1>
                <p className="text-secondary-300 text-base sm:text-lg max-w-xl mx-auto">
                    Join the Peck Pirates Esports Team or the new Production & Broadcasting Crew!
                </p>
            </div>

            {/* Primary Signup Action Card (Google Form) */}
            <div className="card border-2 border-yellow-400/80 bg-gradient-to-br from-yellow-500/10 via-primary-950/40 to-secondary-900 mb-8 p-6 sm:p-8 shadow-2xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="text-center md:text-left flex-1">
                        <span className="bg-yellow-400 text-primary-950 text-xs font-extrabold px-2.5 py-1 rounded uppercase tracking-wider">
                            Step 1: Interest & Signup Form
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mt-2 mb-1">
                            Complete the Online Signup Form
                        </h2>
                        <p className="text-secondary-300 text-sm leading-relaxed">
                            Takes less than 2 minutes. Fill out your student information, parent contact, grade (8–12), and select the competitive games or production roles you're interested in.
                        </p>
                    </div>
                    <div className="shrink-0 w-full md:w-auto">
                        <a
                            href="https://forms.gle/2GvpB888G5xRci1S9"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-yellow-400 hover:bg-yellow-300 text-primary-950 font-extrabold text-base px-6 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-yellow-400/30 ring-2 ring-yellow-300 flex items-center justify-center gap-2 w-full"
                        >
                            <span>Open Google Signup Form</span>
                            <span className="text-sm">↗</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Info Meeting Card */}
            <div className="card border-primary-500/40 bg-secondary-900 mb-8 p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <div className="text-xs font-bold text-primary-400 uppercase tracking-wider mb-1">
                            Step 2: Parent & Player Info Meeting
                        </div>
                        <h3 className="text-xl font-bold text-white mb-1">Tuesday, September 1, 2026</h3>
                        <p className="text-secondary-300 text-sm">
                            Open Lab Drop-in: <strong className="text-white">3:00 PM – 5:00 PM</strong> • Presentation: <strong className="text-white">4:30 PM</strong>
                        </p>
                        <p className="text-secondary-400 text-xs mt-1">
                            📍 Esports Lab / High School Media Center
                        </p>
                    </div>
                    <span className="bg-primary-500/20 text-primary-300 border border-primary-500/30 text-xs px-3 py-1.5 rounded-lg font-semibold shrink-0">
                        Drop-in Anytime!
                    </span>
                </div>
            </div>

            {/* Season Timeline */}
            <section className="mb-8">
                <h2 className="text-xl font-display font-bold text-white mb-4">📆 Season Timeline</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="card text-center p-3 border-l-4 border-l-yellow-400">
                        <div className="text-xs font-bold text-yellow-400 uppercase">Sept 1</div>
                        <div className="text-sm font-bold text-white mt-1">Info Meeting</div>
                        <div className="text-xs text-secondary-400">3–5 PM Drop-in</div>
                    </div>
                    <div className="card text-center p-3 border-l-4 border-l-primary-500">
                        <div className="text-xs font-bold text-primary-400 uppercase">Sept 7–14</div>
                        <div className="text-sm font-bold text-white mt-1">Signups Close</div>
                        <div className="text-xs text-secondary-400">Due Sept 14 @ 3 PM</div>
                    </div>
                    <div className="card text-center p-3 border-l-4 border-l-blue-400">
                        <div className="text-xs font-bold text-blue-400 uppercase">Sept 7–18</div>
                        <div className="text-sm font-bold text-white mt-1">Pre-Season</div>
                        <div className="text-xs text-secondary-400">Lab practice</div>
                    </div>
                    <div className="card text-center p-3 border-l-4 border-l-green-400">
                        <div className="text-xs font-bold text-green-400 uppercase">Sept 21 – Nov 6</div>
                        <div className="text-sm font-bold text-white mt-1">Regular Season</div>
                        <div className="text-xs text-secondary-400">Mon–Wed 3–5 PM</div>
                    </div>
                    <div className="card text-center p-3 border-l-4 border-l-purple-400 col-span-2 sm:col-span-1">
                        <div className="text-xs font-bold text-purple-400 uppercase">Dec 5</div>
                        <div className="text-sm font-bold text-white mt-1">State Finals</div>
                        <div className="text-xs text-secondary-400">UM-Flint</div>
                    </div>
                </div>
            </section>

            {/* Documents & Downloads */}
            <section className="mb-10" aria-labelledby="documents-heading">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 id="documents-heading" className="text-xl font-display font-bold text-white">📋 Program Documents & Handouts</h2>
                        <p className="text-xs text-secondary-300">Official information, athletic eligibility, and code of conduct</p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    {/* Season Packet */}
                    <a
                        href="/documents/Peck_Pirates_Esports_Fall_2026_Packet.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Download Fall 2026 Season Packet PDF (opens in a new tab)"
                        className="card flex items-start gap-3 hover:border-primary-500/60 transition-colors border-primary-500/30 group bg-secondary-900 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
                    >
                        <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center shrink-0 text-xl group-hover:scale-105 transition-transform" aria-hidden="true">
                            📄
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white text-sm">Fall 2026 Season Packet (PDF)</h3>
                            <p className="text-xs text-secondary-300 mt-0.5">
                                Complete official packet: Handbook, Code of Conduct & Combined Auth Form
                            </p>
                            <span className="text-xs text-primary-400 font-semibold mt-1 inline-block">Download PDF →</span>
                        </div>
                    </a>

                    {/* Code of Conduct */}
                    <a
                        href="/documents/Code-of-Conduct-Fall-2026.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Download Code of Conduct and Participant Authorization Form PDF (opens in a new tab)"
                        className="card flex items-start gap-3 hover:border-primary-500/60 transition-colors border-primary-500/30 group bg-secondary-900 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
                    >
                        <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center shrink-0 text-xl group-hover:scale-105 transition-transform" aria-hidden="true">
                            📜
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white text-sm">Code of Conduct & Auth Form (PDF)</h3>
                            <p className="text-xs text-secondary-300 mt-0.5">
                                Student & parent signature form (PIRATE code of conduct)
                            </p>
                            <span className="text-xs text-primary-400 font-semibold mt-1 inline-block">Download PDF →</span>
                        </div>
                    </a>

                    {/* Production Team One-Pagers */}
                    <a
                        href="/documents/production-team-signups.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="View Production Team Roles Guide (opens in a new tab)"
                        className="card flex items-start gap-3 hover:border-yellow-500/60 transition-colors border-yellow-500/30 group bg-secondary-900 focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:outline-none"
                    >
                        <div className="w-10 h-10 bg-yellow-400/20 rounded-lg flex items-center justify-center shrink-0 text-xl group-hover:scale-105 transition-transform" aria-hidden="true">
                            🎬
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white text-sm">Production Team Roles Guide</h3>
                            <p className="text-xs text-secondary-300 mt-0.5">
                                Printable 6-role description guide for broadcasters, tech, & graphics
                            </p>
                            <span className="text-xs text-yellow-400 font-semibold mt-1 inline-block">View Handout →</span>
                        </div>
                    </a>

                    {/* Printable Flyer */}
                    <a
                        href="/documents/fall-2026-info-meeting.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="View Fall 2026 Information Handout (opens in a new tab)"
                        className="card flex items-start gap-3 hover:border-yellow-500/60 transition-colors border-yellow-500/30 group bg-secondary-900 focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:outline-none"
                    >
                        <div className="w-10 h-10 bg-yellow-400/20 rounded-lg flex items-center justify-center shrink-0 text-xl group-hover:scale-105 transition-transform" aria-hidden="true">
                            🖨️
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white text-sm">Fall 2026 Info Handout</h3>
                            <p className="text-xs text-secondary-300 mt-0.5">
                                Season overview flyer with meeting details and schedule
                            </p>
                            <span className="text-xs text-yellow-400 font-semibold mt-1 inline-block">View Handout →</span>
                        </div>
                    </a>
                </div>
            </section>


            {/* Frequently Asked Questions */}
            <section className="mb-10 card p-6 bg-secondary-900 border-secondary-700">
                <h2 className="text-xl font-display font-bold text-white mb-4">💬 Frequently Asked Questions</h2>
                <div className="space-y-4 text-sm">
                    <div>
                        <h3 className="font-bold text-white">Who is eligible to join?</h3>
                        <p className="text-secondary-400 mt-0.5">
                            Any Peck student in Grades 8 through 12 (ages 13+). No prior competitive gaming or streaming experience is required!
                        </p>
                    </div>
                    <div>
                        <h3 className="font-bold text-white">What if I play another sport (Football, Cross Country, Cheer)?</h3>
                        <p className="text-secondary-400 mt-0.5">
                            Dual-sport athletes are 100% welcome! We coordinate schedules with your athletic coaches so you can participate in both sports without conflict.
                        </p>
                    </div>
                    <div>
                        <h3 className="font-bold text-white">How much does it cost?</h3>
                        <p className="text-secondary-300 mt-0.5 leading-relaxed">
                            The program is 100% free to join with zero participation fees or athletic dues. All lab gaming PCs, tournament consoles, monitors, broadcast gear, and regular season league entries are covered by the school. Optional custom team jerseys and special off-campus travel opportunities (like State Finals or college tours) may have additional costs that are subsidized through team fundraising efforts.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-bold text-white">What games and roles are offered this fall?</h3>
                        <p className="text-secondary-400 mt-0.5">
                            Competition centers on <strong>Super Smash Bros. Ultimate</strong> (Solos & 3v3 Crews), with additional titles evaluated at the Sept 1 meeting. Our new <strong>Production & Broadcasting Crew</strong> offers non-gaming roles in casting, streaming tech, and graphics/social media.
                        </p>
                    </div>
                    <div>
                        <h3 className="font-bold text-white">Do I need to bring my own gear?</h3>
                        <p className="text-secondary-400 mt-0.5">
                            All PCs, monitors, broadcast switchers, and consoles are provided in the school Esports Lab. Smash players may optionally bring their own Nintendo Switch or controller.
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact */}
            <section className="text-center pt-4 border-t border-secondary-800">
                <h2 className="text-lg font-semibold text-white mb-1">Have Questions?</h2>
                <p className="text-secondary-400 text-sm mb-3">Coach Brian Anklam is available to answer any questions.</p>
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                    <a href="mailto:anklamb@peckschools.net" className="text-primary-400 hover:underline">
                        ✉️ anklamb@peckschools.net
                    </a>
                    <a href="tel:8103780645" className="text-primary-400 hover:underline">
                        📞 (810) 378-0645
                    </a>
                </div>
                <div className="mt-6">
                    <Link to="/" className="text-xs text-secondary-400 hover:text-white transition-colors">
                        ← Back to Home Page
                    </Link>
                </div>
            </section>
        </div>
    );
}

